//
// 注意：
//      flutter 工程需要提前在 window.__mb_nativeConfig 下进行 module 注入，格式如下：
//
// ```
// window.__mb_nativeConfig = {
//     NativeTest: {
//         methods: [
//             'test1'
//         ],
//         constants: {},
//     },
//     FilePicker: {
//         methods: [
//             'pickFiles'
//         ],
//         constants: {}
//     }
// };
// ```
//
// 支持两种方式，以上述注入内容为例：
// 1)
//  const logger = window.NativeModules.Logger;
//  logger.log('The string is from js!');
//
// 2)
//  const logger = window.Logger;
//  logger.log('The string is from js!');
//

/**
 * 在进行索引查询时，如果指定的索引值不存在，将返回此常量
 */
const NOT_FOUND = -1;

/**
 * native 侧技术栈枚举
 */
const TechMode = {
  native: 'native',
  flutter: 'flutter',
  unknown: 'unknown',
};

/**
 * 系统及 web 内核类型
 */
const SystemType = {
  unknown: 'unknown',
  iOS_webkit: '1',
  android_webkit: '2',
  macOS_webkit: '3',
  windows_webview2: '4',
  macOS_cef: '5',
  windows_cef: '6',
  iOS_lorenzo: '7', // flutter_inappwebview
  android_lorenzo: '8', // flutter_inappwebview
};

/**
 * 获取 {@link value} 的类型
 */
const getValueType = function (value) {
  return Object.prototype.toString
    .call(value)
    .replace(/\s|object|\[|\]/g, '')
    .toLowerCase();
};

class MoonBridgeDescriptor {
  constructor() {
    this._techMode = this._getTechMode();
    this._systemType = this._getSystemType();
    this._nativeConfig = this._getNativeConfig();
  }

  get techMode() {
    return this._techMode;
  }

  get systemType() {
    return this._systemType;
  }

  get nativeConfig() {
    return this._nativeConfig;
  }

  // MARK - Internal Methods

  // 'native' | 'flutter'
  _getTechMode() {
    if (this._getTechModeByUserAgent() !== TechMode.unknown) {
      return this._getTechModeByUserAgent();
    }
    if (this._getTechModeByInjection() !== TechMode.unknown) {
      return this._getTechModeByInjection();
    }
    return TechMode.native;
  }

  _getSystemType() {
    if (this._getSystemTypeByUserAgent() !== SystemType.unknown) {
      return this._getSystemTypeByUserAgent();
    }
    if (this._getSystemTypeByInjection() !== SystemType.unknown) {
      return this._getSystemTypeByInjection();
    }
    return SystemType.unknown;
  }

  _getNativeConfig() {
    return this._getNativeConfigByInjection();
  }

  // MARK - Read By User-Agent

  _getTechModeByUserAgent() {
    let userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.indexOf('techmode/flutter') > NOT_FOUND) {
      return TechMode.flutter;
    } else if (userAgent.indexOf('techmode/native') > NOT_FOUND) {
      return TechMode.native;
    }
    return TechMode.unknown;
  }

  _getSystemTypeByUserAgent() {
    let userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.indexOf('systemtype/ios') > NOT_FOUND) {
      return SystemType.iOS_webkit;
    } else if (userAgent.indexOf('systemtype/android') > NOT_FOUND) {
      return SystemType.android_webkit;
    } else if (userAgent.indexOf('systemtype/macos') > NOT_FOUND) {
      return SystemType.macOS_webkit;
    } else if (userAgent.indexOf('systemtype/windows') > NOT_FOUND) {
      return SystemType.windows_webview2;
    } else if (userAgent.indexOf('systemtype/cef-macos') > NOT_FOUND) {
      return SystemType.macOS_cef;
    } else if (userAgent.indexOf('systemtype/cef-windows') > NOT_FOUND) {
      return SystemType.windows_cef;
    } else if (userAgent.indexOf('systemtype/inapp-ios') > NOT_FOUND) {
      return SystemType.iOS_lorenzo;
    } else if (userAgent.indexOf('systemtype/inapp-android') > NOT_FOUND) {
      return SystemType.android_lorenzo;
    }

    return SystemType.unknown;
  }

  // MARK - Read By Injection

  _getTechModeByInjection() {
    if (getValueType(window.__mb_techMode) === 'string') {
      return window.__mb_techMode;
    }
    return TechMode.unknown;
  }

  _getSystemTypeByInjection() {
    let systemType;
    if (
      getValueType(window.__mb_systemType) === 'number' ||
      getValueType(window.__mb_systemType) === 'string'
    ) {
      systemType = window.__mb_systemType;
    } else if (
      getValueType(window.MoonBridge) === 'object' &&
      getValueType(window.MoonBridge.getSystemType) === 'function'
    ) {
      systemType = window.MoonBridge.getSystemType();
    } else if (
      getValueType(window.__MBHYSystemType) === 'number' ||
      getValueType(window.__MBHYSystemType) === 'string'
    ) {
      systemType = window.__MBHYSystemType;
    } else if (
      getValueType(window.MoonBridge) === 'object' &&
      getValueType(window.MoonBridge.getMBSystemType) === 'function'
    ) {
      systemType = window.MoonBridge.getMBSystemType();
    } else {
      // unknown
      systemType = SystemType.unknown;
    }
    return String(systemType);
  }

  _getNativeConfigByInjection() {
    let nativeConfig;
    if (getValueType(window.__mb_nativeConfig) === 'object') {
      nativeConfig = window.__mb_nativeConfig;
    } else if (
      getValueType(window.MoonBridge) === 'object' &&
      getValueType(window.MoonBridge.getNativeConfig) === 'function'
    ) {
      nativeConfig = JSON.parse(window.MoonBridge.getNativeConfig());
    } else if (getValueType(window.__MBHYNativeConfig) === 'object') {
      nativeConfig = window.__MBHYNativeConfig;
    } else if (
      getValueType(window.MoonBridge) === 'object' &&
      getValueType(window.MoonBridge.getMBConfig) === 'function'
    ) {
      nativeConfig = JSON.parse(window.MoonBridge.getMBConfig());
    } else {
      // unknown
      nativeConfig = {};
    }
    return nativeConfig;
  }
}

class JSMessagePoster {
  constructor(techMode, systemType) {
    this._techMode = techMode;
    this._systemType = systemType;
  }

  postMessage(message) {
    if (this._techMode === TechMode.flutter) {
      this._postFlutterMessage(message);
    } else {
      this._postNativeMessage(message);
    }
  }

  _postFlutterMessage(message) {
    switch (this._systemType) {
      case SystemType.iOS_webkit:
        {
          window.webkit.messageHandlers.MoonBridge.postMessage(JSON.stringify(message));
        }
        break;
      case SystemType.android_webkit:
        {
          window.MoonBridge.postMessage(JSON.stringify(message));
        }
        break;
      case SystemType.macOS_webkit:
        {
          window.webkit.messageHandlers.MoonBridge.postMessage(message);
        }
        break;
      case SystemType.windows_webview2:
        {
          // 在 Windows 平台上，由于所使用的 WebView2 内核的限制，
          // iframe 中的子窗口无法正常接收消息，因此需要通过顶层窗口 (window.top)
          // 来发送消息至 WebView2 内核，以确保消息被正确传递和处理
          window.top.chrome.webview.postMessage(JSON.stringify(message));
        }
        break;
      case SystemType.macOS_cef:
        {
          window.CefFlutterJSBridge.callHandler(message);
        }
        break;
      case SystemType.windows_cef:
        {
          window.CefFlutterJSBridge.callHandler(message);
        }
        break;
      case SystemType.iOS_lorenzo:
        {
          window.flutter_inappwebview.callHandler('MoonBridge', message);
        }
        break;
      case SystemType.android_lorenzo:
        {
          // bugfix:
          //  https://github.com/pichillilorenzo/flutter_inappwebview/issues/218
          if (window.flutter_inappwebview.callHandler) {
            window.flutter_inappwebview.callHandler('MoonBridge', message);
          } else {
            window.flutter_inappwebview._callHandler(
              'MoonBridge',
              setTimeout(function () {
                // Do nothing...
              }),
              JSON.stringify([message]),
            );
          }
        }
        break;
      default:
        {
          console.error('ERROR: Unknown platform!');
        }
        break;
    }
  }

  _postNativeMessage(message) {
    switch (this._systemType) {
      case SystemType.iOS_webkit:
        {
          window.webkit.messageHandlers.MoonBridge.postMessage(JSON.stringify(message));
        }
        break;
      case SystemType.android_webkit:
        {
          window.MoonBridge.postMessage(JSON.stringify(message));
        }
        break;
      default:
        {
          console.error('ERROR: Unknown platform!');
        }
        break;
    }
  }
}

class MoonBridge {
  constructor() {
    this._callbacksMap = {};

    let descriptor = new MoonBridgeDescriptor();
    this._techMode = descriptor.techMode;
    this._systemType = descriptor.systemType;

    this._available = this._systemType !== SystemType.unknown;
    if (this._available) {
      this._registerModules(descriptor.nativeConfig);
    }

    this._messagePoster = new JSMessagePoster(this._techMode, this._systemType);
  }

  /**
   * 获取 MoonBridge 功能的可用状态
   * 此属性用于检查 MoonBridge 功能是否已成功初始化，如果初始化成功，表明相关的功能可用
   *
   * @returns {boolean} 表示 MoonBridge 功能可用状态的布尔值。
   *                    如果返回 true，则表示功能已初始化且可用；
   *                    如果返回 false，则表示功能未初始化或不可用。
   */
  get available() {
    return this._available;
  }

  /**
   * 由 native 侧调用，用于处理来自 native 环境的响应并执行相应的 JavaScript 回调函数，
   * 该函数通常作为桥接 native 代码和 JavaScript 之间通信的一部分
   *
   * @param {string} callbackId - 用于标识和检索存储的回调函数的唯一标识符。
   *                             必须是一个有效的字符串，对应之前传递给 native 环境的回调。
   * @param {any} response - native 环境返回的响应数据。这可以是任何类型的数据，
   *                         但通常是对象或字符串格式的 JSON 数据。
   * @param {boolean} finished - 指示是否在调用回调函数后从存储中删除回调的标志。
   *                             如果为 true，回调将被执行并从回调映射中删除，
   *                             防止其被再次调用。
   */
  invokeCallback(callbackId, response, finished) {
    if (getValueType(callbackId) !== 'string') {
      return console.error('ERROR: callbackId is not a `string`!');
    }

    const callback = this._callbacksMap[callbackId];
    if (finished === true) {
      delete this._callbacksMap[callbackId];
    }

    if (getValueType(callback) !== 'function') {
      return console.error('ERROR: Callback function is not exist!');
    }

    let parsedResponse = response;
    if (getValueType(parsedResponse) === 'string') {
      try {
        parsedResponse = JSON.parse(parsedResponse);
      } catch (error) {
        parsedResponse = null;
        console.error('ERROR: Parse response error!');
      }
    }

    callback.apply(null, parsedResponse);
  }

  // MARK - Internal Methods

  /**
   * 注册模块到对象及 window 对象上
   * @param {Object} modulesMap - 包含模块配置的对象
   */
  _registerModules(modulesMap) {
    if (getValueType(modulesMap) !== 'object') {
      return console.error('ERROR: modulesMap is not an object!');
    }

    // 遍历 modulesMap 中的每个模块
    Object.keys(modulesMap).forEach((moduleName) => {
      // 延迟加载自定义模块实例，如果尚未存在则创建
      this[moduleName] = this[moduleName] || {};
      window[moduleName] = this[moduleName];

      const moduleConfig = modulesMap[moduleName];

      // 分配模块的常量
      if (moduleConfig.constants) {
        Object.assign(this[moduleName], moduleConfig.constants);
      }

      // 分配模块的方法
      if (Array.isArray(moduleConfig.methods)) {
        moduleConfig.methods.forEach((methodName) => {
          const methodImplementation = (...args) => {
            this._callNativeFunction(moduleName, methodName, args);
          };

          // 将方法分配给模块，并同时映射到 window 对象上
          this[moduleName][methodName] = methodImplementation;
          window[moduleName][methodName] = methodImplementation;
        });
      }
    });
  }

  /**
   * 调用 native 侧方法
   */
  _callNativeFunction(moduleName, methodName, args) {
    const message = this._wrapMessage(moduleName, methodName, args);
    this._messagePoster.postMessage(message);
  }

  /**
   * 使用约定格式包装消息内容
   */
  _wrapMessage(moduleName, methodName, args) {
    const nativeArguments = args.map((arg) => {
      // 如果参数是函数，生成回调 ID 并存储函数引用
      if (getValueType(arg) === 'function') {
        const callbackID = this._generateCallbackId();
        this._callbacksMap[callbackID] = arg;
        return callbackID;
      }
      return arg;
    });

    // 使用属性简写构造消息对象
    const message = { moduleName, methodName, arguments: nativeArguments };
    return message;
  }

  /**
   * 生成 callbackId
   */
  _generateCallbackId() {
    let uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0,
        v = c == 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });

    return '_$_mb_callback_$_' + uuid + '_$_winidxs_$_' + this._generateWindowIndexs();
  }

  /**
   * 生成多层嵌套 iframe 的索引
   *
   * 当页面中有多层嵌套的 iframe 时，本方法用于生成表示当前 iframe 在整个嵌套结构中位置的索引字符串。
   *
   * iframe 嵌套结构示例:
   *
   *     [main]                <- 顶层 window
   *       |
   *       v
   *     [1a, 1b, 1c, 1d, 1e]  <- 第一层嵌套的 iframe
   *       |
   *       v
   *     [2a, 2b, 2c]          <- 第二层嵌套的 iframe
   *       |
   *       v
   *     [3a, 3b, 3c, 3d]      <- 第三层嵌套的 iframe
   *
   * 索引生成规则:
   * - 每层 iframe 的索引是基于它在父层 iframe 集合中的位置
   * - 索引以 '0' 开始，从左到右
   * - 索引通过下划线 ('_') 连接，表示从顶层到当前层的路径
   *
   * 例如:
   * - 假设当前 iframe 的路径是 [main, 1a, 2b, 3c]，其索引将是 '0_1_2'
   *   这表示它是从顶层开始，第一层的第一个 iframe (1a)，第二层的第二个 iframe (2b)，以及第三层的第三个 iframe (3c)
   *
   * 特殊情况:
   * - 如果当前 window 是顶层 window，则索引固定返回 'top'
   */
  _generateWindowIndexs() {
    // 检查当前 window 是否为顶层 window
    if (window.self == window.top) {
      return 'top';
    }

    let win = window;
    let indexsString = '';

    while (win.self != win.top && win.self != win.parent) {
      // 获取当前 window 的父 window 中所有 iframe 元素
      let iframes = win.parent.document.getElementsByTagName('iframe');
      // 在父 window 的 iframe 列表中找到当前 window 的索引位置
      let index = Array.prototype.indexOf.call(iframes, win.frameElement);
      // indexs 拼接
      indexsString = indexsString === '' ? String(index) : index + '_' + indexsString;
      // 更新 win 为父 window，以便在下次循环中处理上一层
      win = win.parent;
    }

    return indexsString === '-1' ? 'top' : indexsString;
  }
}

class NativeChannel {
  /**
   * 创建一个 NativeChannel 对象
   *
   * @param {string} channelName - 通道名称。
   * @param {function} channelFunc - 通道函数，用于处理通道消息。
   */
  constructor(channelName, channelFunc) {
    this.channelName = channelName;
    this.channelFunc = channelFunc;
  }
}

/**
 * NativeChannelManager 用于管理 {@link NativeChannel}
 * 由 js 侧进行 {@link NativeChannel} 的添加和删除，native 侧进行消息的通知
 */
class NativeChannelManager {
  constructor() {
    this._nativeChannels = {};
  }

  /**
   * 添加一个 NativeChannel 到管理器中
   * @param {NativeChannel} channel - 要添加的 NativeChannel 对象
   */
  addChannel(channel) {
    if (!(channel instanceof NativeChannel)) {
      return console.error('ERROR: argument channel must be NativeChannel class!');
    }

    this._nativeChannels[channel.channelName] = channel;
  }

  /**
   * 从管理器中移除指定名称的 NativeChannel
   * @param {string} channelName - 要移除的 NativeChannel 的名称。
   */
  removeChannel(channelName) {
    delete this._nativeChannels[channelName];
  }

  /**
   * native 侧调用，通知指定名称的 NativeChannel 的通道函数，并向其传递数据
   *
   * @param {string} channelName - 要调用的 NativeChannel 的名称
   * @param {any} nativeArgs - 传递给通信渠道的数据
   */
  sendMessageToChannel(channelName, nativeArgs) {
    if (getValueType(channelName) !== 'string') {
      return console.error('ERROR: channelName is not a `string`!');
    }

    const channel = this._nativeChannels[channelName];
    if (!channel) {
      return;
    }

    let parsedArgs = nativeArgs;
    if (getValueType(parsedArgs) === 'string') {
      try {
        parsedArgs = JSON.parse(parsedArgs);
      } catch (error) {
        parsedArgs = null;
        console.error('ERROR: parse arguments error!');
      }
    }

    channel.channelFunc.apply(null, parsedArgs);
  }
}

/**
 * 负责常规的 moonbridge 通信
 */
const nativeModules = new MoonBridge();
window.nativeModules = window.NativeModules = nativeModules;

/**
 * 负责管理与 native 交互的常驻通道
 *
 * 诸如 IM 通信等业务场景可以使用 {@link NativeChannelManager.addChannel} 来进行
 * 通信的长期监听，来代替对自定义 module 中 callback 参数的长期持有
 */
const nativeChannels = new NativeChannelManager();
window.nativeChannels = nativeChannels;
